import styled from '@emotion/styled';
import { CardBorder } from '@shared/components/core/card/CommonCardStyles';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import { BodyMedium, BodySmall, TitleXSmall } from '@shared/components/core/text/Typography';
import * as skillsApi from '@shared/api/skills/skillsApi';
import * as mcpApi from '@shared/api/mcp/mcpServersApi';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IOrgExtensionUsageManagerProps {
    organizationId: string;
    canViewSkills: boolean;
    canViewConnectors: boolean;
}

/** Normalized usage row across the skill/connector twins. */
interface UsageRow {
    id: string;
    displayName?: string;
    installCount: number;
    members: {
        userId: string;
        userDisplayName?: string;
        userEmail?: string;
        isAccountWide: boolean;
        enabledDate: string;
    }[];
}

export default function OrgExtensionUsageManager({
    organizationId,
    canViewSkills,
    canViewConnectors,
}: Readonly<IOrgExtensionUsageManagerProps>) {
    const { t } = useTranslation();
    const [skillUsage, setSkillUsage] = useState<UsageRow[]>([]);
    const [connectorUsage, setConnectorUsage] = useState<UsageRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                if (canViewSkills) {
                    const result = await skillsApi.getOrganizationSkillUsage(organizationId);
                    if (result.resultType === 'Ok' && result.data) {
                        setSkillUsage(
                            result.data.map((u) => ({
                                id: u.agentSkillId,
                                displayName: u.displayName,
                                installCount: u.installCount,
                                members: u.members,
                            }))
                        );
                    }
                }
                if (canViewConnectors) {
                    const result = await mcpApi.getOrganizationMcpServerUsage(organizationId);
                    if (result.resultType === 'Ok' && result.data) {
                        setConnectorUsage(
                            result.data.map((u) => ({
                                id: u.mcpServerRegistrationId,
                                displayName: u.displayName,
                                installCount: u.installCount,
                                members: u.members,
                            }))
                        );
                    }
                }
            } catch {
                useAlertsStore.getState().showError(t('common:organization.governance.extensions.usage.loadError'));
            } finally {
                setIsLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, canViewSkills, canViewConnectors]);

    if (isLoading) return <SpacedLoader />;

    return (
        <Wrap>
            <BodyMedium>{t('common:organization.governance.extensions.usage.description')}</BodyMedium>
            {canViewSkills && (
                <UsageSection
                    title={t('common:organization.governance.extensions.usage.skillsTitle')}
                    rows={skillUsage}
                />
            )}
            {canViewConnectors && (
                <UsageSection
                    title={t('common:organization.governance.extensions.usage.connectorsTitle')}
                    rows={connectorUsage}
                />
            )}
        </Wrap>
    );
}

function UsageSection({ title, rows }: Readonly<{ title: string; rows: UsageRow[] }>) {
    const { t } = useTranslation();
    return (
        <>
            <SubSectionRow title={title} variant="small" />
            {rows.length === 0 ? (
                <BodySmall>{t('common:organization.governance.extensions.usage.empty')}</BodySmall>
            ) : (
                rows.map((row) => (
                    <UsageCard key={row.id}>
                        <UsageHeader>
                            <TitleXSmall>
                                {row.displayName ?? t('common:organization.governance.extensions.usage.unnamed')}
                            </TitleXSmall>
                            <BodySmall>
                                {t('common:organization.governance.extensions.usage.installCount', {
                                    count: row.installCount,
                                })}
                            </BodySmall>
                        </UsageHeader>
                        <MemberList>
                            {row.members.map((member) => (
                                <MemberRow key={`${row.id}-${member.userId}`}>
                                    <BodySmall>{member.userDisplayName || member.userEmail || member.userId}</BodySmall>
                                    <MemberMeta>
                                        {member.isAccountWide
                                            ? t('common:organization.governance.extensions.usage.accountWide')
                                            : t('common:organization.governance.extensions.usage.perDevice')}
                                    </MemberMeta>
                                </MemberRow>
                            ))}
                        </MemberList>
                    </UsageCard>
                ))
            )}
        </>
    );
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1.5rem;
`;

const UsageCard = styled(CardBorder)`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
`;

const UsageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

const MemberList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const MemberRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.25rem 0;
    border-top: 1px solid ${(props) => props.theme.color.border};
`;

const MemberMeta = styled(BodySmall)`
    color: ${(props) => props.theme.color.textBody};
    flex: none;
`;
